import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import type { Conference, User } from '@viq-admin/types';

/**
 * Cross-tenant lookup lists: every conference and every user, whatever the
 * "Operate as" scope says. The header search matches against them, and pages
 * use them to turn the ids on a row (a lead's conferenceId, say) into names.
 *
 * `meta: { companyId: null }` is the dataProvider's explicit opt-out of the
 * global scope (see getList in api/dataProvider.ts): without it a scoped
 * operator would only find their scoped tenant's records.
 *
 * Both lists are small and change rarely, so a few minutes' staleness is
 * fine; react-query shares one fetch between every caller. A scope switch
 * still refetches them (CompanyScopeProvider invalidates every query), which
 * is harmless.
 */

export const LOOKUP_STALE_MS = 5 * 60_000;

export type UserLookupRow = User & { companyName?: string | null };

export function useAllConferences(opts: { enabled?: boolean } = {}) {
  const { result, query } = useList<Conference>({
    resource: 'conferences',
    pagination: { mode: 'off' },
    meta: { companyId: null },
    queryOptions: { staleTime: LOOKUP_STALE_MS, enabled: opts.enabled ?? true },
  });
  return {
    conferences: result?.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useAllUsers(opts: { enabled?: boolean } = {}) {
  const { result, query } = useList<UserLookupRow>({
    resource: 'users',
    pagination: { mode: 'off' },
    meta: { companyId: null },
    queryOptions: { staleTime: LOOKUP_STALE_MS, enabled: opts.enabled ?? true },
  });
  return {
    users: result?.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** conferenceId → name, for rows that carry only the id (leads, telemetry). */
export function useConferenceNameMap(): Map<string, string> {
  const { conferences } = useAllConferences();
  return useMemo(() => new Map(conferences.map((c) => [c.id, c.name])), [conferences]);
}
